<template>
    <div class="pt-wrap">
        <div class="pt-toolbar">
            <el-input v-model="query" size="small" clearable
                      prefix-icon="el-icon-search" placeholder="搜索转写内容…"></el-input>
            <div class="pt-keywords" v-if="keywords && keywords.length">
                <el-tag v-for="k in keywords" :key="k" size="mini" effect="plain"
                        class="pt-kw" @click="query = k">{{ k }}</el-tag>
            </div>
        </div>

        <div class="pt-list" ref="list" @wheel="pauseFollow" @touchmove="pauseFollow">
            <template v-if="visibleSentences.length">
                <div v-for="s in visibleSentences" :key="s.key"
                     class="pt-sentence" :class="{active: s.key === activeKey}"
                     :data-key="s.key"
                     @click="handlePick(s)">
                    <div class="pt-meta">
                        <span class="pt-speaker" v-if="s.speaker">{{ s.speaker }}</span>
                        <span class="pt-time">{{ s.time }}</span>
                    </div>
                    <div class="pt-text" v-html="query ? highlight(s.text) : s.esc"></div>
                </div>
            </template>
            <div class="pt-empty" v-else-if="!sentences.length">
                转写尚未生成,稍后再试
            </div>
            <div class="pt-empty" v-else>没有匹配"{{ query }}"的内容</div>
        </div>

        <div class="pt-locate" v-if="!following && !query">
            <el-button size="mini" type="primary" round icon="el-icon-position"
                       @click="resumeFollow">回到当前位置</el-button>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'playback-transcript',
        props: {
            transcript: {default: null},
            keywords: {type: Array, default: () => []},
            currentMs: {type: Number, default: 0},
        },
        data() {
            return {
                query: '',
                following: true,
            };
        },
        computed: {
            // 拍平成句子列表:{key, speaker, start_time, text, time(预计算), esc(预转义)}
            sentences() {
                const list = [];
                const paras = (this.transcript && this.transcript.paragraphs) || [];
                paras.forEach((p, pi) => {
                    const speaker = (p.speaker_info && (p.speaker_info.username || p.speaker_info.nickname))
                        || (p.speaker && p.speaker.user_name) || '';
                    (p.sentences || []).forEach((s, si) => {
                        const text = (s.words || []).map(w => w.text || '').join('');
                        if (!text) return;
                        const start = s.start_time != null ? s.start_time : (p.start_time || 0);
                        list.push({
                            key: pi + '-' + si,
                            speaker: speaker,
                            start_time: start,
                            text: text,
                            time: this.formatMs(start),      // 预计算,避免每次重渲染逐句调用
                            esc: this.escapeHtml(text),      // 预转义,无搜索词时直接使用
                        });
                    });
                });
                // 一次性排序兜底:activeKey 的 break 扫描依赖升序;腾讯输出通常已升序
                list.sort((a, b) => a.start_time - b.start_time);
                return list;
            },
            visibleSentences() {
                if (!this.query) return this.sentences;
                const q = this.query.toLowerCase();
                return this.sentences.filter(s => s.text.toLowerCase().indexOf(q) !== -1);
            },
            activeKey() {
                let key = null;
                for (const s of this.sentences) {
                    if (this.currentMs >= s.start_time) key = s.key;
                    else break;
                }
                return key;
            }
        },
        watch: {
            activeKey() {
                if (this.following && !this.query) this.scrollToActive();
            },
            query(val) {
                // 清空搜索立即回到播放位置
                if (!val && this.following) this.scrollToActive();
            }
        },
        methods: {
            formatMs(ms) {
                const total = Math.floor((parseInt(ms, 10) || 0) / 1000);
                const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
                return (h ? h + ':' + (m < 10 ? '0' : '') : '') + m + ':' + (s < 10 ? '0' : '') + s;
            },
            escapeHtml(s) {
                return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            },
            highlight(text) {
                const esc = this.escapeHtml(text);
                if (!this.query) return esc;
                const q = this.escapeHtml(this.query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                try {
                    return esc.replace(new RegExp('(' + q + ')', 'gi'), '<mark>$1</mark>');
                } catch (e) {
                    return esc;
                }
            },
            scrollToActive() {
                this.$nextTick(() => {
                    const list = this.$refs.list;
                    if (!list || !this.activeKey) return;
                    const el = list.querySelector('[data-key="' + this.activeKey + '"]');
                    if (!el) return;
                    el.scrollIntoView({block: 'center', behavior: 'smooth'});
                });
            },
            // 只有真实用户动作(滚轮/触摸)才暂停跟随;程序滚动不会触发这两类事件
            pauseFollow() {
                this.following = false;
            },
            // 点击句子跳播并恢复自动跟随
            handlePick(s) {
                this.$emit('seek', s.start_time);
                this.following = true;
            },
            resumeFollow() {
                this.following = true;
                this.scrollToActive();
            }
        }
    };
</script>

<style lang="scss">
.pt-wrap {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    .pt-toolbar {
        padding: 4px 2px 8px;

        .pt-keywords { margin-top: 6px; }
        .pt-kw { margin: 0 6px 6px 0; cursor: pointer; }
    }

    .pt-list {
        flex: 1;
        min-height: 0; // flex 子项防内容撑开
        overflow-y: auto;
        padding-bottom: 40px;

        .pt-sentence {
            padding: 6px 8px;
            border-radius: 6px;
            cursor: pointer;
            margin-bottom: 2px;

            &:hover { background: #f5f7fa; }
            &.active { background: #ecf5ff; }

            .pt-meta {
                font-size: 12px; color: #909399; margin-bottom: 2px;
                .pt-speaker { color: #409EFF; margin-right: 8px; }
            }
            .pt-text {
                font-size: 13px; line-height: 1.7; color: #303133;
                mark { background: #ffe58f; padding: 0 1px; }
            }
        }

        .pt-empty { color: #909399; text-align: center; padding: 40px 0; }
    }

    .pt-locate {
        position: absolute;
        bottom: 12px;
        right: 12px;
    }
}
</style>
